/**
 * The entry gate's latency measurement, and the arithmetic behind it.
 *
 * This measures the GAME path — a Socket.IO application-level round trip —
 * because that is what decides whether an interaction feels instant, and
 * because it works solo, before there is a peer to connect to. The voice
 * path's latency is measured separately during the round by `usePeerVoice`
 * and recorded as `NETWORK_SAMPLE`.
 *
 * The median rather than the mean: one 900 ms outlier from a Wi-Fi retransmit
 * should not fail a participant whose connection is otherwise fine, and the
 * mean is exactly the statistic that lets it.
 */

export interface LatencyResult {
  samples: number[]
  /** Median of the samples that came back, in milliseconds. */
  medianMs: number
  /** Samples requested but never answered within the timeout. */
  lost: number
  requested: number
}

export type LatencyVerdict = 'pass' | 'fail_slow' | 'fail_unstable'

/**
 * Even counts average the two middle values, which is the standard definition
 * and matters here because the default sample count is even.
 */
export function median(values: readonly number[]): number {
  if (values.length === 0) return NaN
  const sorted = [...values].sort((a, b) => a - b)
  const mid = sorted.length >> 1
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

/**
 * More than a third of the pings lost is a different failure from "slow", and
 * it deserves different words on screen: packet loss on the game path breaks
 * an interaction outright rather than delaying it.
 */
export const MAX_LOSS_FRACTION = 1 / 3

export function classify(result: LatencyResult, maxMedianMs: number): LatencyVerdict {
  if (result.samples.length === 0) return 'fail_unstable'
  if (result.lost / result.requested > MAX_LOSS_FRACTION) return 'fail_unstable'
  return result.medianMs <= maxMedianMs ? 'pass' : 'fail_slow'
}

export function summarise(samples: readonly number[], requested: number): LatencyResult {
  return {
    samples: [...samples],
    medianMs: median(samples),
    lost: requested - samples.length,
    requested,
  }
}

// ---------------------------------------------------------------------------
// The sampler
// ---------------------------------------------------------------------------

/** The slice of a socket this needs. Keeps the helper testable with a fake. */
export interface PingTransport {
  ping(sentAt: number): void
  onPong(handler: (sentAt: number) => void): () => void
}

export interface SampleOptions {
  count: number
  timeoutMs: number
  /** Gap between pings. Back-to-back pings measure queueing, not latency. */
  intervalMs?: number
  now?: () => number
  onProgress?: (done: number, total: number) => void
}

/**
 * Send `count` pings one at a time and collect the round trips.
 *
 * Serial rather than parallel on purpose: a burst of twelve pings shares one
 * TCP window and reports the queueing delay of the burst, which is not the
 * number a single keypress will experience.
 */
export async function sampleLatency(
  transport: PingTransport,
  options: SampleOptions,
): Promise<LatencyResult> {
  const now = options.now ?? (() => performance.now())
  const interval = options.intervalMs ?? 120
  const samples: number[] = []

  for (let i = 0; i < options.count; i += 1) {
    const sentAt = now()
    const rtt = await new Promise<number | null>((resolve) => {
      let settled = false
      const finish = (value: number | null): void => {
        if (settled) return
        settled = true
        unsubscribe()
        clearTimeout(timer)
        resolve(value)
      }
      const timer = setTimeout(() => finish(null), options.timeoutMs)
      const unsubscribe = transport.onPong((echoed) => {
        // A late pong from an earlier round must not be credited to this one.
        if (echoed !== sentAt) return
        finish(now() - sentAt)
      })
      transport.ping(sentAt)
    })

    if (rtt !== null) samples.push(rtt)
    options.onProgress?.(i + 1, options.count)
    if (i + 1 < options.count) await new Promise((resolve) => setTimeout(resolve, interval))
  }

  return summarise(samples, options.count)
}
